import React, { lazy } from 'react';
import { useTranslation } from 'react-i18next';
import { Field } from 'formik';
import { FormikDatePicker } from 'globalcomponents/UI/Form/Base';
import { UI_DATE_LOWER_FORMAT } from 'globalconfig/constants/static';
import { getDateLocale, phoneNumberValidation } from 'globalutils/fmUtils';
import { checkServiceProvider } from './serviceProviderConfig';

const layout = window.$environment?.CURRENT_LAYOUT;
const Input = lazy(() => import(`globalcomponents/UI/Input/${layout}`));
const Label = lazy(() => import(`globalcomponents/UI/FormLabel/${layout}`));
const Select = lazy(() => import(`globalcomponents/UI/Select/${layout}`));
const FormStyle = require(`globalcomponents/UI/Form/${layout}/serverStyle`).default;

export default function RightForm({ Styled, values, handleEdit, showError, errors, isNotValid, hasSaveApiStatus, states }) {
  const i18n = useTranslation();

  return (
    <Styled.rightWrapper>
      <div className="companyForm">
        <FormStyle.FieldsWrapper>
          <Label htmlFor="address" variant="body1">
            {i18n.t('common.address')}
          </Label>
          <FormStyle.TextWrapper>
            <Field
              name="address"
              id="address"
              handleOnChange={(event) => handleEdit(event, { field: 'address' })}
              as={Input}
              placeholder={i18n.t('common.address')}
              value={values['address']}
              type="text"
            />
            {showError('address', errors, isNotValid, hasSaveApiStatus?.errorMessage)}
          </FormStyle.TextWrapper>
        </FormStyle.FieldsWrapper>
        <FormStyle.FieldsWrapper>
          <Label htmlFor="city" variant="body1">
            {i18n.t('common.city')}
          </Label>
          <FormStyle.TextWrapper>
            <Field
              name="city"
              id="city"
              handleOnChange={(event) => handleEdit(event, { field: 'city' })}
              as={Input}
              placeholder={i18n.t('common.city')}
              value={values['city']}
              type="text"
            />
            {showError('city', errors, isNotValid, hasSaveApiStatus?.errorMessage)}
          </FormStyle.TextWrapper>
        </FormStyle.FieldsWrapper>
        <FormStyle.FieldsWrapper>
          <Label htmlFor="state" variant="body1">
            {i18n.t('common.state')}
          </Label>
          <FormStyle.TextWrapper>
            <Field
              name="state"
              id="state"
              onChange={(event) => {
                handleEdit(event, { field: 'state' });
              }}
              as={Select}
              placeholderText={i18n.t('common.state')}
              value={values['state']}
              suggestions={states}
            />
            {showError('state', errors, isNotValid, hasSaveApiStatus?.errorMessage)}
          </FormStyle.TextWrapper>
        </FormStyle.FieldsWrapper>
        <FormStyle.FieldsWrapper>
          <Label htmlFor="zipCode" variant="body1">
            {i18n.t('common.zipCode')}
          </Label>
          <FormStyle.TextWrapper>
            <Field
              name="zipCode"
              id="zipCode"
              handleOnChange={(event) => handleEdit(event, { field: 'zipCode' })}
              as={Input}
              placeholder={i18n.t('common.zipCode')}
              value={values['zipCode']}
              type="text"
            />
            {showError('zipCode', errors, isNotValid, hasSaveApiStatus?.errorMessage)}
          </FormStyle.TextWrapper>
        </FormStyle.FieldsWrapper>
        <FormStyle.FieldsWrapper>
          <Label htmlFor="companyPhone" variant="body1">
            {i18n.t('common.phone')}
          </Label>
          <FormStyle.TextWrapper>
            <Field
              name="companyPhone"
              id="companyPhone"
              handleOnChange={(event) => handleEdit(event, { field: 'companyPhone' })}
              as={Input}
              placeholder={i18n.t('common.phone')}
              value={values['companyPhone']}
              type="text"
              updateValue={phoneNumberValidation}
            />
            {showError('companyPhone', errors, isNotValid, hasSaveApiStatus?.errorMessage)}
          </FormStyle.TextWrapper>
        </FormStyle.FieldsWrapper>
        <FormStyle.FieldsWrapper width="100%">
          <Label variant="body1" htmlFor="insuranceExpDate">
            {i18n.t('companyData.expiryDate')}
          </Label>
          <FormStyle.TextWrapper className="userForm">
            <Field
              name="insuranceExpDate"
              onChange={(event) => {
                handleEdit(event, {
                  field: 'insuranceExpDate',
                  type: 'date',
                });
                // setNewValues('hireDate', event)
              }}
              locale={getDateLocale()}
              as={FormikDatePicker}
              placeholderText={UI_DATE_LOWER_FORMAT}
              value={values['insuranceExpDate']}
              type="date"
              showPopperArrow={false}
              showTimeSelect={false}
              showDisabledMonthNavigation
              showMonthDropdown
              showYearDropdown
              dropdownMode="select"
              isClearable={true}
              minDate={new Date()}
            />
            {showError('insuranceExpDate', errors, isNotValid, hasSaveApiStatus?.errorMessage)}
          </FormStyle.TextWrapper>
        </FormStyle.FieldsWrapper>

        {checkServiceProvider('rightform_fields_speedLimit', window.$environment.SERVICE_PROVIDER, false) && (
          <FormStyle.FieldsWrapper>
            <Label htmlFor="speedLimit" variant="body1">
              {i18n.t('companies.speedLimit')}
            </Label>
            <FormStyle.TextWrapper>
              <Field
                name="speedLimit"
                id="speedLimit"
                handleOnChange={(event) => handleEdit(event, { field: 'speedLimit' })}
                as={Input}
                placeholder={i18n.t('companies.speedLimit')}
                value={values['speedLimit']}
                type="number"
              />
              {showError('speedLimit', errors, isNotValid, hasSaveApiStatus?.errorMessage)}
            </FormStyle.TextWrapper>
          </FormStyle.FieldsWrapper>
        )}
      </div>
      <div className="companyForm">
        <FormStyle.FieldsWrapper>
          <Label htmlFor="contactPerson" variant="body1">
            {i18n.t('companyData.contactPerson')}
          </Label>
          <FormStyle.TextWrapper>
            <Field
              name="contactPerson"
              id="contactPerson"
              handleOnChange={(event) => handleEdit(event, { field: 'contactPerson' })}
              as={Input}
              placeholder={i18n.t('companyData.contactPerson')}
              value={values['contactPerson']}
              type="text"
            />
            {showError('contactPerson', errors, isNotValid, hasSaveApiStatus?.errorMessage)}
          </FormStyle.TextWrapper>
        </FormStyle.FieldsWrapper>

        <FormStyle.FieldsWrapper>
          <Label htmlFor="otherLocationContactPerson" variant="body1">
            {i18n.t('companyData.otherLocationContactPerson')}
          </Label>
          <FormStyle.TextWrapper>
            <Field
              name="otherLocationContactPerson"
              id="otherLocationContactPerson"
              handleOnChange={(event) =>
                handleEdit(event, {
                  field: 'otherLocationContactPerson',
                })
              }
              as={Input}
              placeholder={i18n.t('companyData.otherLocationContactPerson')}
              value={values['otherLocationContactPerson']}
              type="text"
            />
            {showError('otherLocationContactPerson', errors, isNotValid, hasSaveApiStatus?.errorMessage)}
          </FormStyle.TextWrapper>
        </FormStyle.FieldsWrapper>

        <FormStyle.FieldsWrapper>
          <Label htmlFor="otherLocationAddress" variant="body1">
            {i18n.t('companyData.otherLocationAddress')}
          </Label>
          <FormStyle.TextWrapper>
            <Field
              name="otherLocationAddress"
              id="otherLocationAddress"
              handleOnChange={(event) => handleEdit(event, { field: 'otherLocationAddress' })}
              as={Input}
              placeholder={i18n.t('companyData.otherLocationAddress')}
              value={values['otherLocationAddress']}
              type="text"
            />
            {showError('otherLocationAddress', errors, isNotValid, hasSaveApiStatus?.errorMessage)}
          </FormStyle.TextWrapper>
        </FormStyle.FieldsWrapper>

        <FormStyle.FieldsWrapper>
          <Label htmlFor="otherLocationPhone" variant="body1">
            {i18n.t('companyData.otherLocationPhoneNumber')}
          </Label>
          <FormStyle.TextWrapper>
            <Field
              name="otherLocationPhone"
              id="otherLocationPhone"
              handleOnChange={(event) => handleEdit(event, { field: 'otherLocationPhone' })}
              as={Input}
              placeholder={i18n.t('companyData.otherLocationPhoneNumber')}
              value={values['otherLocationPhone']}
              type="text"
            />
            {showError('otherLocationPhone', errors, isNotValid, hasSaveApiStatus?.errorMessage)}
          </FormStyle.TextWrapper>
        </FormStyle.FieldsWrapper>

        <FormStyle.FieldsWrapper>
          <Label htmlFor="website" variant="body1">
            {i18n.t('companyData.website')}
          </Label>
          <FormStyle.TextWrapper>
            <Field
              name="website"
              id="website"
              handleOnChange={(event) => handleEdit(event, { field: 'website' })}
              as={Input}
              placeholder={i18n.t('companyData.website')}
              value={values['website']}
              type="text"
            />
            {showError('website', errors, isNotValid, hasSaveApiStatus?.errorMessage)}
          </FormStyle.TextWrapper>
        </FormStyle.FieldsWrapper>
        {checkServiceProvider('rightform_fields_zohoId', window?.$environment?.SERVICE_PROVIDER, false) ? (
          <>
            <FormStyle.FieldsWrapper>
              <Label htmlFor="zohoId" variant="body1">
                {i18n.t('Zoho Customer Id')}
              </Label>
              <FormStyle.TextWrapper>
                <Field
                  name="zohoId"
                  id="zohoId"
                  handleOnChange={(event) => handleEdit(event, { field: 'zohoId' })}
                  as={Input}
                  value={values['zohoId']}
                  type="text"
                />
                {showError('zohoId', errors, isNotValid, hasSaveApiStatus?.errorMessage)}
              </FormStyle.TextWrapper>
            </FormStyle.FieldsWrapper>
          </>
        ) : (
          <></>
        )}
      </div>
    </Styled.rightWrapper>
  );
}
